import {
  type HandleUploadFunction,
  type ProfileImageCropperContextData,
} from '../types.js';
import { actionCreatorFactory } from './actionCreators.js';
import {
  CONTEXT_TYPE_AVATAR,
  UPDATE_CONTEXT_PARAMETERS,
} from './actionTypes.js';
import { initialState, ProfileImageCropperReducer } from './reducer.js';
import {
  createContext,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useMemo,
  useReducer,
} from 'react';

const nullContextObject = {
  [CONTEXT_TYPE_AVATAR]: null,
};

export const ProfileImageCropperContext =
  createContext<ProfileImageCropperContextData>({
    context: CONTEXT_TYPE_AVATAR,
    cropIsProcessing: false,
    croppedAreaPixels: nullContextObject,
    croppedPreview: nullContextObject,
    // eslint-disable-next-line unicorn/consistent-function-scoping
    handleUpload: () => () => {},
    imageFile: null,
    imageName: null,
    imageType: null,
    isCropping: false,
    isLoading: false,
    originalImageSize: nullContextObject,
    saveProfileOnUpload: false,
    uploadImage: nullContextObject,
  } as ProfileImageCropperContextData);

export const ProfileImageCropperContextProvider = ({
  children,
}: {
  readonly children: ReactNode;
}) => {
  const [state, dispatch] = useReducer<ProfileImageCropperReducer>(
    ProfileImageCropperReducer,
    { ...initialState },
  );
  const actions = actionCreatorFactory(dispatch);

  const handleUpload = useCallback<HandleUploadFunction>(
    (
      inputElement,
      { context = CONTEXT_TYPE_AVATAR, shouldSaveProfileOnUpload = false } = {},
    ) =>
      (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        if (inputElement.current) {
          dispatch({
            payload: {
              context,
              saveProfileOnUpload: shouldSaveProfileOnUpload,
            },
            type: UPDATE_CONTEXT_PARAMETERS,
          });
          inputElement.current.click();
        }
      },
    [],
  );

  const providerContext = useMemo<ProfileImageCropperContextData>(() => {
    return {
      ...state,
      ...actions,
      handleUpload,
    };
  }, [actions, handleUpload, state]);

  return (
    <ProfileImageCropperContext.Provider value={providerContext}>
      {children}
    </ProfileImageCropperContext.Provider>
  );
};
